//@ts-check
/**
 * _Their Things_ (wip-archive/social/SOCIAL_PHASE2_SHARING.md): everything the people you follow
 * share with you, as one feed, newest first, and who shares it and on which lists, for filtering it
 * by person and by list without asking the server again; and gifting what is on it
 * (wip-archive/social/SOCIAL_PHASE3_RESERVING.md). With it, the feed on Home, `activity`
 * (wip-archive/social/SOCIAL_PHASE4_HOME_FEED.md). Online only: nothing here is kept on the device.
 */
import ky from "ky";
import { getfetchopts } from "$lib/utils/utils.js";
import { apiBase } from "$lib/env.js";
import { failure } from "$lib/me.js";

const basePath = apiBase();
const SHARED_URL = `${basePath}/api/things/shared`;

/**
 * @typedef {import("$lib/people.svelte.js").Person} Person
 * @typedef {{ listId: string, name: string, date: string | null, others: Person[] }} SharedListInfo
 * @typedef {Person & { lists: SharedListInfo[] }} Sharer
 * @typedef {null | { mine: true, surprise: boolean, bought: boolean } | { mine: false, by: Person | null, guest?: string }} Gift
 *   `guest`: someone gifting from the list's link, by the name they typed (wip-archive/social/SOCIAL_PHASE3B_THE_LINK.md)
 * @typedef {{ thingId: string, ownerId: string, listIds: string[], shared: string, state: string, gift: Gift } & Record<string, any>} SharedThing
 * @typedef {{ kind: "shared", id: string, at: string, ownerId: string, listId: string, count: number }
 *   | { kind: "added", id: string, at: string, ownerId: string, listId: string, thingIds: string[] }
 *   | { kind: "got", id: string, at: string, ownerId: string, thingId: string }
 *   | { kind: "gifting", id: string, at: string, ownerId: string, thingId: string, by: Person | null, guest?: string }
 *   | { kind: "gifting_you", id: string, at: string, thingId: string }
 *   | { kind: "follower", id: string, at: string, userId: string, person: Person }} Activity
 *   An item of the feed on Home, newest first. Someone else's things are in `things`; a
 *   `gifting_you` names a thing of your own.
 */

/**
 * @type {{ people: Sharer[], things: SharedThing[], activity: Activity[], loaded: boolean, loading: boolean, reason: "" | "offline" | "failed" }}
 */
export const shared = $state({ people: [], things: [], activity: [], loaded: false, loading: false, reason: "" });

/** The thing whose sheet is open on Their Things, if any: a copy, since it is read only. */
export const viewingShared = $state({ thing: /** @type {SharedThing | null} */ (null) });

/** @type {Promise<void> | null} */
let inflight = null;

/**
 * Everything shared with you. Whatever is on screen stays while it loads; a second call while one
 * is running is the same answer, so a page of photos whose signatures lapsed together asks once.
 */
export function loadShared() {
  inflight ??= readShared().finally(() => (inflight = null));
  return inflight;
}

async function readShared() {
  shared.loading = true;
  try {
    const fetchopts = await getfetchopts();
    // Your time zone, so things put on a list group by your day, not the server's.
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const res = /** @type {any} */ (await ky.get(`${SHARED_URL}?tz=${encodeURIComponent(tz)}`, fetchopts).json());
    const people = /** @type {Sharer[]} */ (res.people || []);
    const things = /** @type {SharedThing[]} */ (res.things || []);
    // A thing's lists in the order the chips show them, so its strip names the same one each time.
    const order = new Map(people.flatMap((person) => person.lists.map((list, i) => [list.listId, i])));
    for (const thing of things) thing.listIds.sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
    shared.people = people;
    shared.things = things;
    shared.activity = /** @type {Activity[]} */ (res.activity || []);
    shared.loaded = true;
    shared.reason = "";
  } catch (err) {
    console.error("Failed to load what is shared", err);
    const reason = failure(err);
    if (reason !== "signed_out") shared.reason = reason;
  } finally {
    shared.loading = false;
  }
}

/** For a pull, and for coming back to the app. */
export const refreshShared = loadShared;

/** @param {string} userId */
export function sharerById(userId) {
  return shared.people.find((person) => person.userId === userId);
}

/**
 * A list you are on, and whose it is.
 *
 * @param {string} listId
 * @returns {{ owner: Sharer, list: SharedListInfo } | undefined}
 */
export function sharedListById(listId) {
  for (const owner of shared.people) {
    const list = owner.lists.find((l) => l.listId === listId);
    if (list) return { owner, list };
  }
  return undefined;
}

/**
 * Leave a list someone put you on. Waits for the server, like the Me sheet's changes: it is about
 * someone else's list, and has to be true when the screen says so. Its things go from the feed,
 * unless they are on another of that person's lists you are still on.
 *
 * @param {string} listId
 * @returns {Promise<{ ok: true } | { ok: false, reason: "offline" | "signed_out" | "failed" }>}
 */
export async function leaveSharedList(listId) {
  try {
    const fetchopts = await getfetchopts();
    await ky.delete(`${SHARED_URL}/${listId}`, fetchopts);
  } catch (err) {
    console.error("Failed to leave the list", err);
    return { ok: false, reason: failure(err) };
  }
  shared.people = shared.people
    .map((person) => ({ ...person, lists: person.lists.filter((list) => list.listId !== listId) }))
    .filter((person) => person.lists.length);
  shared.things = shared.things
    .map((thing) => ({ ...thing, listIds: thing.listIds.filter((id) => id !== listId) }))
    .filter((thing) => thing.listIds.length);
  // What you were gifting and no longer see is let go on the server, unless bought: read it again.
  void loadShared();
  return { ok: true };
}

/**
 * What you are gifting, the soonest first: by the nearest date of the lists each is on, then the
 * undated, newest first as in the feed.
 *
 * @returns {SharedThing[]}
 */
export function myGifts() {
  const soonest = (/** @type {SharedThing} */ thing) =>
    thing.listIds
      .map((id) => sharedListById(id)?.list.date)
      .filter(Boolean)
      .sort()[0] ?? "9999";
  return shared.things
    .filter((thing) => thing.gift?.mine)
    .map((thing, i) => ({ thing, i, date: soonest(thing) }))
    .sort((a, b) => (a.date === b.date ? a.i - b.i : a.date < b.date ? -1 : 1))
    .map(({ thing }) => thing);
}

/**
 * The thing everywhere it is shown: the feed, and its sheet if open.
 *
 * @param {string} thingId
 * @param {Gift} gift
 */
function setGift(thingId, gift) {
  shared.things = shared.things.map((thing) => (thing.thingId === thingId ? { ...thing, gift } : thing));
  if (viewingShared.thing?.thingId === thingId) viewingShared.thing = { ...viewingShared.thing, gift };
}

/**
 * _I'm gifting this_, or a change to it: whether it is a surprise, whether it is bought. Waits for
 * the server — one giver per thing is the server's to decide. Someone else got there first, or the
 * owner has it now, and the feed is read again so the screen says so.
 *
 * @param {SharedThing} thing
 * @param {{ surprise: boolean, bought: boolean }} choice
 * @returns {Promise<{ ok: true } | { ok: false, reason: "taken" | "got" | "gone" | "offline" | "signed_out" | "failed" }>}
 */
export async function giftShared(thing, choice) {
  try {
    const fetchopts = await getfetchopts({ json: choice });
    const res = /** @type {any} */ (await ky.put(giftUrl(thing), fetchopts).json());
    setGift(thing.thingId, res.gift);
    return { ok: true };
  } catch (err) {
    const status = /** @type {any} */ (err)?.response?.status;
    const code = /** @type {any} */ (err)?.body?.code;
    if (status === 409 && (code === "taken" || code === "got")) {
      await loadShared();
      return { ok: false, reason: code };
    }
    if (status === 404) {
      await loadShared();
      return { ok: false, reason: "gone" };
    }
    console.error("Failed to gift", err);
    return { ok: false, reason: failure(err) };
  }
}

/**
 * Let go of a gift of yours: it is free for someone else.
 *
 * @param {SharedThing} thing
 * @returns {Promise<{ ok: true } | { ok: false, reason: "offline" | "signed_out" | "failed" }>}
 */
export async function letGoShared(thing) {
  try {
    const fetchopts = await getfetchopts();
    await ky.delete(giftUrl(thing), fetchopts);
    setGift(thing.thingId, null);
    return { ok: true };
  } catch (err) {
    console.error("Failed to let go of the gift", err);
    return { ok: false, reason: failure(err) };
  }
}

/** @param {SharedThing} thing */
const giftUrl = (thing) => `${SHARED_URL}/${thing.ownerId}/things/${thing.thingId}/gift`;

/** @param {SharedThing} thing */
export function openSharedThing(thing) {
  viewingShared.thing = thing;
}

export function closeSharedThing() {
  viewingShared.thing = null;
}
